/**
 * Repair Context Utilities
 * 
 * Determines user's context (owner/assigned vendor) relative to a repair based on organization relationships.
 * NOT based on user roles - assigned vendor/owner are organizational contexts, not roles.
 */

/**
 * Check if user is in "owner context" for a repair
 * Owner context = user's organization OWNS the repair (repair.company_id matches user's company)
 * 
 * @param {object} user - User object with companyId or organizationId
 * @param {object} repair - Repair object with company_id
 * @returns {boolean} True if user's organization owns the repair
 */
export const isCustomerContext = (user, repair) => {
  if (!user || !repair) return false;
  
  // User's company matches repair's company_id
  const userCompanyId = user.companyId || user.company_id;
  const repairCompanyId = repair.company_id || repair.companyId;
  
  return userCompanyId && repairCompanyId && userCompanyId === repairCompanyId;
};

/**
 * Check if user is in "assigned vendor context" for a repair
 * Assigned vendor context = user's organization MATCHES the repair's assigned vendor (repair.vendor_id matches user's organization)
 * 
 * @param {object} user - User object with organizationId
 * @param {object} repair - Repair object with vendor_id
 * @returns {boolean} True if user's organization is assigned as vendor for the repair
 */
export const isVendorContext = (user, repair) => {
  if (!user || !repair) return false;
  
  // User's organization matches repair's vendor_id (vendor_id is organization UUID)
  const userOrgId = user.organizationId || user.organization_id;
  const repairVendorId = repair.vendor_id || repair.vendorId;
  
  return userOrgId && repairVendorId && userOrgId === repairVendorId;
};

/**
 * Get user's context for a repair
 * @param {object} user - User object
 * @param {object} repair - Repair object
 * @returns {object} { isCustomer, isVendor }
 */
export const getRepairContext = (user, repair) => {
  return {
    isCustomer: isCustomerContext(user, repair),
    isVendor: isVendorContext(user, repair)
  };
};
